import React, { useRef, useEffect, useState } from 'react'
import { useLoader } from '@react-three/fiber'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'
import { TextureLoader, MeshStandardMaterial, MeshPhysicalMaterial, sRGBEncoding, BackSide, FrontSide, DoubleSide, Color, CubeTextureLoader } from 'three'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

// Simple mobile detection
const isMobile = () => {
  if (typeof window === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

export function Mari({ shouldClap, onClapComplete, awakeningMode }) {
  // Return null immediately if on mobile
  if (isMobile()) return null

  // Load all models and animations
  const model = useLoader(FBXLoader, '/mari_model.fbx')
  const texturesModel = useLoader(FBXLoader, '/mari33.fbx')
  const sittingAnim = useLoader(FBXLoader, '/sitting.fbx')
  const clapAnim = useLoader(FBXLoader, '/clap.fbx')
  const pointAnim = useLoader(FBXLoader, '/point.fbx')
  
  const groupRef = useRef()
  const mixerRef = useRef()
  const [currentAnimation, setCurrentAnimation] = useState('sitting')
  const [isTransitioning, setIsTransitioning] = useState(false)
  const transitionProgress = useRef(0)
  const sittingAction = useRef()
  const clapAction = useRef()
  const pointAction = useRef()

  // Load all textures
  const textureLoader = new TextureLoader()
  const textures = {
    // Body textures
    body: {
      base: textureLoader.load('/textures/IST Cirrha Body.jpg', undefined, undefined, (err) => console.error('Error loading body base texture:', err)),
      normal: textureLoader.load('/textures/IST Cirrha Body NORM.png', undefined, undefined, (err) => console.error('Error loading body normal texture:', err)),
      roughness: textureLoader.load('/textures/IST Cirrha Body ROUGH.jpg', undefined, undefined, (err) => console.error('Error loading body roughness texture:', err))
    },
    face: {
      base: textureLoader.load('/textures/IST Cirrha Face.jpg', undefined, undefined, (err) => console.error('Error loading face base texture:', err)),
      normal: textureLoader.load('/textures/IST Cirrha Face NORM.png', undefined, undefined, (err) => console.error('Error loading face normal texture:', err)),
      roughness: textureLoader.load('/textures/IST Cirrha Face ROUGH.jpg', undefined, undefined, (err) => console.error('Error loading face roughness texture:', err))
    },
    arm: {
      base: textureLoader.load('/textures/IST Cirrha Arm.jpg'),
      normal: textureLoader.load('/textures/IST Cirrha Arm NORM.png'),
      roughness: textureLoader.load('/textures/IST Cirrha Arm ROUGH.jpg')
    },
    leg: {
      base: textureLoader.load('/textures/IST Cirrha Leg.jpg'),
      normal: textureLoader.load('/textures/IST Cirrha Leg NORM.png'),
      roughness: textureLoader.load('/textures/IST Cirrha Leg ROUGH.jpg')
    },
    // Hair textures
    hair: {
      base: textureLoader.load('/textures/Carme_Hr_00_0.jpg'),
      bump: textureLoader.load('/textures/Carme_Hr_Bm.jpg'),
      opacity: textureLoader.load('/textures/Carme_Hr_Op.jpg'),
      roughness: textureLoader.load('/textures/Carme_Hr_Rg.jpg')
    },
    scalp: {
      base: textureLoader.load('/textures/Carme_Sc_00_0.jpg'),
      bump: textureLoader.load('/textures/Carme_Sc_Bm.jpg'),
      opacity: textureLoader.load('/textures/Carme_Sc_Op.jpg')
    },
    // Eyes and face details
    eyes: {
      base: textureLoader.load('/textures/IST Cirrha Eyes 05.jpg'),
      normal: textureLoader.load('/textures/IST Cirrha Eyes NORM.jpg')
    },
    mouth: {
      base: textureLoader.load('/textures/IST Cirrha Mouth.jpg'),
      specular: textureLoader.load('/textures/IST Cirrha MouthS.jpg'),
      normal: textureLoader.load('/textures/IST Cirrha Mouth NORM.jpg')
    },
    eyelashes: textureLoader.load('/textures/IST Cirrha Eyelashes.jpg'),
    tear: {
      base: textureLoader.load('/textures/IST Cirrha TearOpacity.jpg')
    },
    nails: {
      base: textureLoader.load('/textures/IST Cirrha Nails.jpg'),
      roughness: textureLoader.load('/textures/IST Cirrha Nails ROUGH.jpg')
    },
    // Suit textures
    suit: {
      base: textureLoader.load('/textures/WD_Suit_Blue_Base_Color.png', undefined, undefined, (err) => console.error('Error loading suit base texture:', err)),
      normal: textureLoader.load('/textures/WD_Suit_Blue_Normal.png', undefined, undefined, (err) => console.error('Error loading suit normal texture:', err)),
      roughness: textureLoader.load('/textures/WD_Suit_Blue_Roughness.png', undefined, undefined, (err) => console.error('Error loading suit roughness texture:', err)),
      metallic: textureLoader.load('/textures/WD_Suit_Blue_Metallic.png', undefined, undefined, (err) => console.error('Error loading suit metallic texture:', err)),
      bump: textureLoader.load('/textures/WD_Suit_Blue_Bump.png', undefined, undefined, (err) => console.error('Error loading suit bump texture:', err))
    },
    boots: {
      base: textureLoader.load('/textures/WD_Boots_Blue_Base_Color.png'),
      normal: textureLoader.load('/textures/WD_Boots_Blue_Normal.png'),
      roughness: textureLoader.load('/textures/WD_Boots_Blue_Roughness.png'),
      metallic: textureLoader.load('/textures/WD_Boots_Blue_Metallic.png'),
      bump: textureLoader.load('/textures/WD_Boots_Blue_Bump.png')
    },
    chestPiece: {
      base: textureLoader.load('/textures/WD_ChestPiece_Blue_Base_Color.png'),
      normal: textureLoader.load('/textures/WD_ChestPiece_Blue_Normal.png'),
      roughness: textureLoader.load('/textures/WD_ChestPiece_Blue_Roughness.png'),
      metallic: textureLoader.load('/textures/WD_ChestPiece_Blue_Metallic.png'),
      bump: textureLoader.load('/textures/WD_ChestPiece_Blue_Bump.png')
    },
    forearm: {
      base: textureLoader.load('/textures/WD_Forearm_Blue_Base_Color.png'),
      normal: textureLoader.load('/textures/WD_Forearm_Blue_Normal.png'),
      roughness: textureLoader.load('/textures/WD_Forearm_Blue_Roughness.png'),
      metallic: textureLoader.load('/textures/WD_Forearm_Blue_Metallic.png'),
      bump: textureLoader.load('/textures/WD_Forearm_Blue_Bump.png')
    },
    shins: {
      base: textureLoader.load('/textures/WD_Shins_Blue_Base_Color.png'),
      normal: textureLoader.load('/textures/WD_Shins_Blue_Normal.png'),
      roughness: textureLoader.load('/textures/WD_Shins_Blue_Roughness.png'),
      metallic: textureLoader.load('/textures/WD_Shins_Blue_Metallic.png'),
      bump: textureLoader.load('/textures/WD_Shins_Blue_Bump.png')
    }
  }

  useEffect(() => {
    if (model && texturesModel && sittingAnim && clapAnim && pointAnim && groupRef.current) {
      console.log("=== Detailed Mesh Names in Base Model ===");
      model.traverse((child) => {
        if (child.isMesh) {
          console.log(`Mesh: ${child.name}`);
          if (child.material) {
            if (Array.isArray(child.material)) {
              console.log(`  - Multiple Materials (${child.material.length} slots):`);
              child.material.forEach((mat, index) => {
                console.log(`    Slot ${index}:`);
                console.log(`      - Name: ${mat.name || 'unnamed'}`);
                console.log(`      - Maps: ${Object.keys(mat).filter(key => key.includes('Map'))}`);
              });
            } else {
              console.log(`  - Material: ${child.material.name || 'unnamed'}`);
              console.log(`  - Maps: ${Object.keys(child.material).filter(key => key.includes('Map'))}`);
            }
          }
        }
      });

      console.log("\n=== Reference Model (mari33.fbx) Mesh Names ===");
      texturesModel.traverse((child) => {
        if (child.isMesh) {
          console.log(`${child.name} (Material: ${child.material ? child.material.name : 'none'})`);
        }
      });

      // Mesh name mapping from main model to reference model
      const meshNameMap = {
        // Use exact mesh names from mari33.fbx
        ND_Boots_6978Shape: 'ND_Boots_6978Shape',
        Cirrha_Eyebrows_77466Shape: 'Cirrha_Eyebrows_77466Shape',
        CarmeHairG9_176823Shape: 'CarmeHairG9_176823Shape',
        Genesis9EyelashesShape: 'Genesis9EyelashesShape',
        Genesis9EyesShape: 'Genesis9EyesShape',
        ND_Forearm_1632Shape: 'ND_Forearm_1632Shape',
        ND_Suit_3289Shape: 'ND_Suit_3289Shape',
        ND_ChestPiece_3178Shape: 'ND_ChestPiece_3178Shape',
        Genesis9TearShape: 'Genesis9TearShape',
        Genesis9Shape: 'Genesis9Shape',
        Genesis9MouthShape: 'Genesis9MouthShape'
      };

      // Remove any existing children
      while (groupRef.current.children.length > 0) {
        groupRef.current.remove(groupRef.current.children[0])
      }
      
      // Scale up the model
      model.scale.set(2, 2, 2)
      
      // Remove gloves mesh by name
      model.traverse((child) => {
        if (
          child instanceof THREE.Mesh &&
          (child.name.includes('Gloves') ||
            child.name.includes('ND_Gloves') ||
            child.name.includes('DW_Gloves'))
        ) {
          if (child.parent) {
            child.parent.remove(child)
          }
        }
      })

      // Build a material map from the reference model
      const materialMap = {}
      texturesModel.traverse((child) => {
        if (child.isMesh && child.material) {
          materialMap[child.name] = child.material;
          console.log(`Found material for ${child.name}:`, child.material.name);
        }
      })

      // Assign materials with debug info
      model.traverse((child) => {
        if (child.isMesh) {
          // Use mapping for reference
          const refName = meshNameMap[child.name] || child.name;
          console.log(`Looking for material for ${child.name} using ref name ${refName}`);

          // --- Custom Eyes Material (with horizontal flip) ---
          if (child.name === 'Genesis9EyesShape') {
            child.material = new THREE.MeshStandardMaterial({
              emissive: new THREE.Color(awakeningMode ? 0xff0000 : 0xffffff),
              emissiveIntensity: awakeningMode ? 2.0 : 1.0,
              side: THREE.FrontSide,
              roughness: 0.0,
              metalness: 0.0
            });
            child.material.needsUpdate = true;
            console.log('Eye material updated with color:', awakeningMode ? 'red' : 'white');
            return;
          }

          // --- Custom Hair Material ---
          if (child.name.includes('CarmeHairG9')) {
            // Load all hair textures
            const hairBase = textureLoader.load('/textures/Carme_Hr_00_0.jpg');
            const hairBump = textureLoader.load('/textures/Carme_Hr_Bm.jpg');
            const hairRoughness = textureLoader.load('/textures/Carme_Hr_Rg.jpg');
            
            const scalpBase = textureLoader.load('/textures/Carme_Sc_00_0.jpg');
            const scalpBump = textureLoader.load('/textures/Carme_Sc_Bm.jpg');
            
            // Create materials for each slot
            const materials = [
              // hair1
              new THREE.MeshStandardMaterial({
                map: hairBase,
                bumpMap: hairBump,
                alphaMap: hairBase,
                roughnessMap: hairRoughness,
                transparent: true,
                side: THREE.DoubleSide,
                roughness: 1.0, // Increased roughness to reduce highlights
                metalness: 0.0,
                color: new THREE.Color(0x000000), // Pure black
                emissive: new THREE.Color(0x000000), // No emission
                specular: new THREE.Color(0x000000) // No specular highlights
              }),
              // hair2
              new THREE.MeshStandardMaterial({
                map: hairBase,
                bumpMap: hairBump,
                alphaMap: hairBase,
                roughnessMap: hairRoughness,
                transparent: true,
                side: THREE.DoubleSide,
                roughness: 1.0,
                metalness: 0.0,
                color: new THREE.Color(0x000000),
                emissive: new THREE.Color(0x000000),
                specular: new THREE.Color(0x000000)
              }),
              // scalp
              new THREE.MeshStandardMaterial({
                map: scalpBase,
                bumpMap: scalpBump,
                alphaMap: scalpBase,
                transparent: true,
                side: THREE.DoubleSide,
                roughness: 1.0,
                metalness: 0.0,
                color: new THREE.Color(0x000000),
                emissive: new THREE.Color(0x000000),
                specular: new THREE.Color(0x000000)
              }),
              // hair3
              new THREE.MeshStandardMaterial({
                map: hairBase,
                bumpMap: hairBump,
                alphaMap: hairBase,
                roughnessMap: hairRoughness,
                transparent: true,
                side: THREE.DoubleSide,
                roughness: 1.0,
                metalness: 0.0,
                color: new THREE.Color(0x000000),
                emissive: new THREE.Color(0x000000),
                specular: new THREE.Color(0x000000)
              }),
              // hair4
              new THREE.MeshStandardMaterial({
                map: hairBase,
                bumpMap: hairBump,
                alphaMap: hairBase,
                roughnessMap: hairRoughness,
                transparent: true,
                side: THREE.DoubleSide,
                roughness: 1.0,
                metalness: 0.0,
                color: new THREE.Color(0x000000),
                emissive: new THREE.Color(0x000000),
                specular: new THREE.Color(0x000000)
              }),
              // strands
              new THREE.MeshStandardMaterial({
                map: hairBase,
                bumpMap: hairBump,
                alphaMap: hairBase,
                roughnessMap: hairRoughness,
                transparent: true,
                side: THREE.DoubleSide,
                roughness: 1.0,
                metalness: 0.0,
                color: new THREE.Color(0x000000),
                emissive: new THREE.Color(0x000000),
                specular: new THREE.Color(0x000000)
              })
            ];
            
            // Apply materials array to the mesh
            child.material = materials;
            console.log(`Applied ${materials.length} hair materials to ${child.name}!`);
            return;
          } else if (child.name.includes('Carme_Sc')) {
            // Scalp textures
            const scalpBase = textureLoader.load('/textures/Carme_Sc_00_0.jpg');
            const scalpBump = textureLoader.load('/textures/Carme_Sc_Bm.jpg');
            const scalpOpacity = textureLoader.load('/textures/Carme_Sc_Op.jpg');
            
            child.material = new THREE.MeshStandardMaterial({
              map: scalpBase,
              bumpMap: scalpBump,
              alphaMap: scalpOpacity,
              transparent: true,
              side: THREE.DoubleSide,
              roughness: 0.8,
              metalness: 0.0
            });
            child.material.needsUpdate = true;
            console.log(`Scalp material applied to ${child.name}!`);
            return;
          }

          // --- All Other Meshes: Use Reference Model Material ---
          const refMaterial = materialMap[refName];
          if (refMaterial) {
            child.material = refMaterial;
            child.material.needsUpdate = true;
            console.log(`✅ Material assigned for mesh: ${child.name} (from ${refName})`);
          } else {
            console.warn(`❌ No material found for mesh: ${child.name} (tried ${refName})`);
          }
        }
      });
      
      // Create animation mixer
      mixerRef.current = new THREE.AnimationMixer(model)
      
      // Create actions for all animations
      const sittingClip = sittingAnim.animations[0]
      const clapClip = clapAnim.animations[0]
      const pointClip = pointAnim.animations[0]
      
      sittingAction.current = mixerRef.current.clipAction(sittingClip)
      clapAction.current = mixerRef.current.clipAction(clapClip)
      pointAction.current = mixerRef.current.clipAction(pointClip)
      
      // Set up sitting animation
      sittingAction.current.setLoop(THREE.LoopRepeat)
      sittingAction.current.play()
      
      // Set up clap animation
      clapAction.current.setLoop(THREE.LoopOnce)
      clapAction.current.clampWhenFinished = true
      
      // Set up point animation
      pointAction.current.setLoop(THREE.LoopOnce)
      pointAction.current.clampWhenFinished = true
      
      groupRef.current.add(model)
    }
  }, [model, texturesModel, sittingAnim, clapAnim, pointAnim, awakeningMode])

  // Handle clap trigger from parent
  useEffect(() => {
    if (shouldClap && !isTransitioning && currentAnimation === 'sitting') {
      setIsTransitioning(true)
      setCurrentAnimation('clap')
      
      // Fade out sitting animation
      sittingAction.current.fadeOut(0.5)
      
      // Fade in clap animation
      clapAction.current.reset()
      clapAction.current.fadeIn(0.5)
      clapAction.current.play()
      
      // Set timeout to transition back to sitting
      setTimeout(() => {
        // Fade out clap animation
        clapAction.current.fadeOut(0.5)
        
        // Fade in sitting animation
        sittingAction.current.reset()
        sittingAction.current.fadeIn(0.5)
        sittingAction.current.play()
        
        setCurrentAnimation('sitting')
        setIsTransitioning(false)
        onClapComplete()
      }, 3000) // Adjust timing based on clap animation length
    }
  }, [shouldClap, isTransitioning, currentAnimation, onClapComplete])

  // Handle click on Mari
  const handleMariClick = (e) => {
    if (!isTransitioning && currentAnimation === 'sitting') {
      setIsTransitioning(true)
      setCurrentAnimation('point')
      
      // Fade out sitting animation
      sittingAction.current.fadeOut(0.5)
      
      // Fade in point animation
      pointAction.current.reset()
      pointAction.current.fadeIn(0.5)
      pointAction.current.play()
      
      // Set timeout to transition back to sitting
      setTimeout(() => {
        // Fade out point animation
        pointAction.current.fadeOut(0.5)
        
        // Fade in sitting animation
        sittingAction.current.reset()
        sittingAction.current.fadeIn(0.5)
        sittingAction.current.play()
        
        setCurrentAnimation('sitting')
        setIsTransitioning(false)
      }, 3000) // Adjust timing based on point animation length
    }
  }

  // Update animation mixer every frame
  useFrame((state, delta) => {
    if (mixerRef.current) {
      mixerRef.current.update(delta)
    }
  })

  return (
    <group
      ref={groupRef}
      position={[1.55, 1.1, -1.1]}
      rotation={[0, -0.3, 0]}
      onClick={handleMariClick}
    />
  )
} 